import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';
import { PublicTemplateProvider } from '../../../context/PublicTemplateContext';
import { PublicNavbar } from '../Navigation';
import api from '../../../services/api';

// Import all section components
import HomeSection from '../Home/Home';
import PublicProfileByStyle from '../Profile/PublicProfileByStyle';
import PublicEducationByStyle from '../Education/PublicEducationByStyle';
import PublicCareerByStyle from '../Career/PublicCareerByStyle';
import PublicSkillsByStyle from '../Profile/PublicSkillsByStyle';
import PublicAwardsByStyle from '../Profile/PublicAwardsByStyle';
import ServicesSection from '../Services/ServicesSection';
import TestimonialsSection from '../Testimonials/TestimonialsSection';
import Contact from '../contact';
import {
  PublicAboutSection,
  PublicFAQ,
  PublicClients,
  PublicLocations,
  PublicHowItWorks,
  PublicWhyChooseUs,
  PublicLegacy,
  PublicGallery,
  PublicTeam,
  PublicPricing,
  PublicStats,
} from '../Business';

// Section component mapping
const SECTION_COMPONENTS = {
  home: HomeSection,
  profile: PublicProfileByStyle,
  about: PublicAboutSection,
  education: PublicEducationByStyle,
  career: PublicCareerByStyle,
  skills: PublicSkillsByStyle,
  services: ServicesSection,
  how_it_works: PublicHowItWorks,
  why_choose_us: PublicWhyChooseUs,
  stats: PublicStats,
  legacy: PublicLegacy,
  awards: PublicAwardsByStyle,
  testimonials: TestimonialsSection,
  gallery: PublicGallery,
  portfolio: PublicGallery, // Portfolio uses gallery component
  faq: PublicFAQ,
  team: PublicTeam,
  clients: PublicClients,
  locations: PublicLocations,
  pricing: PublicPricing,
  contact: Contact,
};

// Section labels for page titles
const SECTION_LABELS = {
  home: 'Home',
  profile: 'About',
  about: 'About Us',
  education: 'Education',
  career: 'Career',
  skills: 'Skills',
  services: 'Services',
  how_it_works: 'How It Works',
  why_choose_us: 'Why Choose Us',
  stats: 'Stats',
  legacy: 'Our Legacy',
  awards: 'Awards',
  testimonials: 'Testimonials',
  gallery: 'Gallery',
  portfolio: 'Portfolio',
  faq: 'FAQ',
  team: 'Our Team',
  clients: 'Clients',
  locations: 'Locations',
  pricing: 'Pricing',
  contact: 'Contact',
};

/**
 * SectionPage - Renders a single section as a full standalone page
 * Used when a section's display mode is set to "page" instead of "inline"
 */
const SectionPage = () => {
  const { username: urlUsername, section } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [settings, setSettings] = useState({});

  // Fetch user settings to know which sections are pages vs inline
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (username) {
          const res = await api.get(`/public/settings/${username}/`);
          setSettings(res.data?.settings || {});
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, [username]);

  // Get section from URL path if not in params (e.g., /gallery -> gallery)
  // Convert URL format (how-it-works) to key format (how_it_works)
  const pathSection = location.pathname.split('/').filter(Boolean).pop() || 'home';
  const sectionKey = (section || pathSection).replace(/-/g, '_');

  const SectionComponent = SECTION_COMPONENTS[sectionKey];

  // Special route mappings (section key -> actual route)
  const ROUTE_MAPPINGS = {
    blogs: '/blog',
    blog: '/blog',
    stories: '/stories',
  };

  // Handle navigation - check if target is a page or inline section
  const handleNavigate = (targetSection) => {
    console.log('SectionPage: handleNavigate called with:', targetSection);

    // Check for special route mappings first
    if (ROUTE_MAPPINGS[targetSection]) {
      console.log('SectionPage: Using route mapping, navigating to:', ROUTE_MAPPINGS[targetSection]);
      navigate(ROUTE_MAPPINGS[targetSection]);
      return;
    }

    const displayMode = settings[`${targetSection}_display`];
    console.log('SectionPage: displayMode for', targetSection, '=', displayMode);

    if (displayMode === 'page') {
      // Navigate to the separate page
      const path = `/${targetSection.replace(/_/g, '-')}`;
      console.log('SectionPage: Navigating to page:', path);
      navigate(path);
    } else {
      // Navigate to home page - the section is inline there
      console.log('SectionPage: Navigating to home (section is inline)');
      navigate('/');
    }
  };

  if (!SectionComponent) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Section Not Found</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)' }}>The section "{sectionKey}" does not exist.</p>
        </div>
      </div>
    );
  }

  return (
    <PublicTemplateProvider>
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: '#fff',
      }}>
        {/* Navigation Bar */}
        <PublicNavbar
          onNavigate={handleNavigate}
          activeSection={sectionKey}
          accentColor="#6366f1"
        />

        {/* Section Content with proper padding for navbar */}
        <div style={{ paddingTop: '80px' }}>

          <section id={`section-${sectionKey}`}>
            <SectionComponent />
          </section>
        </div>

        {/* Simple Footer */}
        <footer style={{
          padding: '2rem',
          backgroundColor: '#0f172a',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          textAlign: 'center',
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem' }}>
            &copy; {new Date().getFullYear()} All rights reserved |
            <a href="https://profile2connect.com" target="_blank" rel="noopener noreferrer"
               style={{ color: '#6366f1', marginLeft: '0.5rem', textDecoration: 'none' }}>
              Powered by Profile2Connect
            </a>
          </p>
        </footer>
      </div>
    </PublicTemplateProvider>
  );
};

export default SectionPage;
